import { z } from 'zod';
import { MONEY_PATTERN, eq, money, sum, ZERO, gt } from '@miraki/money';
import { zId } from './primitives.js';

/**
 * Retailer-specific payment terms — replaces the single global 30/30/30/10.
 *
 * A retailer's terms are 2–4 stages, each opening one of the four EXISTING workflow gates; no new
 * gate can be introduced. A gate with no stage of its own inherits the cumulative percentage of the
 * stage before it (90/10 => ready and ship also need 90). Terms are versioned and frozen onto an
 * order at approval, so a revision never reaches an order already approved.
 *
 * Percentages are decimal strings, never numbers (money invariant).
 */
export const PAYMENT_GATES = ['beginManufacture', 'markReady', 'ship', 'markDelivered'] as const;
export const zPaymentGate = z.enum(PAYMENT_GATES);
export type PaymentGate = z.infer<typeof zPaymentGate>;

/*
 * There is deliberately NO platform default schedule. A retailer's payment terms exist only once an
 * authorised admin has set them (and the retailer has accepted them); until then they are
 * unconfigured, and nothing in the system assumes a split on their behalf.
 */

export const zPaymentTermsStage = z.object({
  gates: zPaymentGate,
  /** This stage's own share, e.g. '90'. Greater than 0. */
  pct: z.string().regex(MONEY_PATTERN, 'must be a decimal percentage'),
});
export type PaymentTermsStage = z.infer<typeof zPaymentTermsStage>;

/** Why a set of stages is invalid, or null. Shared so the form and the API say the same thing. */
export function paymentTermsProblem(stages: readonly PaymentTermsStage[]): string | null {
  if (stages.length < 2 || stages.length > 4) return 'Use between 2 and 4 payment stages.';
  const order = stages.map((s) => PAYMENT_GATES.indexOf(s.gates));
  for (let i = 1; i < order.length; i++) {
    if (order[i]! <= order[i - 1]!) return 'Each gate may be used once, in workflow order.';
  }
  if (stages[0]!.gates !== 'beginManufacture') return 'The first stage must be paid before manufacturing begins.';
  if (stages[stages.length - 1]!.gates !== 'markDelivered') return 'The last stage must be paid before delivery.';
  const pcts = stages.map((s) => money(s.pct));
  if (pcts.some((p) => !gt(p, ZERO))) return 'Every percentage must be greater than 0.';
  if (!eq(sum(pcts), money('100'))) return 'The percentages must total exactly 100%.';
  return null;
}

export const zPaymentTermsInput = z.object({
  stages: z.array(zPaymentTermsStage).min(2).max(4),
  /** Shown to the retailer on the review screen. */
  reason: z.string().trim().max(500).optional(),
}).superRefine((v, ctx) => {
  const problem = paymentTermsProblem(v.stages);
  if (problem) ctx.addIssue({ code: 'custom', path: ['stages'], message: problem });
});
export type PaymentTermsInput = z.infer<typeof zPaymentTermsInput>;

/** One version of a retailer's terms, as the admin and the retailer see it. */
export const zPaymentTermsVersion = z.object({
  id: zId,
  version: z.number().int().min(1),
  stages: z.array(zPaymentTermsStage),
  reason: z.string().nullable(),
  createdAt: z.string(),
  acceptedAt: z.string().nullable(),
});
export type PaymentTermsVersion = z.infer<typeof zPaymentTermsVersion>;

export const zRetailerPaymentTerms = z.object({
  retailerId: zId,
  /** False until an admin has set terms for this retailer. Unconfigured is not a schedule. */
  configured: z.boolean(),
  /** What new orders are frozen with: the newest ACCEPTED version. Empty while none is accepted. */
  effective: z.array(zPaymentTermsStage),
  /** A newer version awaiting acceptance; while set, the retailer's portal is locked. */
  pending: zPaymentTermsVersion.nullable(),
  history: z.array(zPaymentTermsVersion),
});
export type RetailerPaymentTerms = z.infer<typeof zRetailerPaymentTerms>;
